import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../AuthGate';

// DDD / ALPS — daily reporting scorecard, modeled on the "DDD & ALPS" template.
// Columns: Metric | Goal | (Example, toggleable) | DDD | ALPS. DDD and ALPS are
// free-text entries the user types per metric, per date + shift. The metric /
// goal / example template is hardcoded (fixed scorecard). Saved per
// DDD_ALPS_<date> / shift, forever.

// Template rows: {section} header, {subhead} sub-header, or {metric,goal,example}.
// Each metric gets a stable `k` (index) used as the storage key for DDD/ALPS.
const TEMPLATE = [
  { section: 'WHS' },
  { metric: 'Announcements:\n (IRB?, Network Actions, upcoming initiatives/projects, etc)', goal: 'N/A', example: 'Audiometric Testing 7/27 - 7/31' },
  { metric: 'Weather Updates', goal: 'N/A', example: 'Freezing temps forecasted \nCurrent day\nHigh: 42\nLow: 31' },
  { metric: 'WTD Injuries', goal: 'N/A', example: '4' },
  { metric: 'Upcoming Network Actions (7 days out)', goal: '', example: '' },
  { metric: 'WTD Recordables (+ any pending)', goal: '<3/week', example: '2 posted, 1 pending' },
  { metric: 'Actions due today', goal: 'N/A', example: 'x Actions\nx - WHS\nx - Days\nx - AM/OM\nx - TOM\nx - Nights' },
  { metric: 'Inspections/Tasks due today', goal: '6', example: '3 inspections - 1 Ops, 1 WHS, 1 TOM\n1 compliance - WHS\n 1 action - login' },
  { metric: 'Hearing Protection Device Inspection (WHS)', goal: '4x Weekly', example: '3/4 Completed' },
  { metric: 'HRO Audits', goal: '1/Week', example: '1/1 Completed' },
  { metric: 'RBI Weekly Goal:', goal: '39 Weekly', example: 'FHD: X\nFHN: X\nBHD: X\nBHN: X\nSR: X' },
  { metric: 'Parking Lot RBI', goal: '4/Week', example: 'WHS: 2/2\nOps: 1/2' },
  { metric: '(ARCs) 70/wk', goal: '70 Weekly', example: 'FHD: xx/15\nFHN: xx/15\nXOVER: xx/10 \nBHD: xx/15 \nBHN: xx/15' },
  { metric: 'Dragonfly Submissions (8/week)', goal: '8/Week', example: 'FHD: x\nFHN: x\nBHD: x\nBHN: x\nTOM: x' },
  { metric: 'Open Dragonfly Submissions', goal: '0', example: 'FHD: x\nFHN: x\nBHD: x\nBHN: x\nTOM: x' },
  { metric: 'Open/Unattended LQ360 Defects', goal: '0', example: '1' },
  { metric: 'Working Well Completion', goal: 'Goal Updated Weekly', example: 'Current Huddle\nx% / x%' },
  { metric: 'SIMBA Completion\nWeekly Goal: 105', goal: '5 per AM Weekly\n105/Week\nSubmissions must be broken down by shift', example: 'x / 105 - at risk\nFHD: x - at risk\nFHN:  x - at risk\nBHD:  x - at risk\nBHN:  x - at risk' },
  { metric: 'SIMBA Body Mechanics\nWeekly Goal: 63', goal: '3 per AM Weekly\n63/Week\nSubmissions must be broken down by shift', example: 'x / 63 - at risk\nFHD: x - at risk\nFHN:  x - at risk\nBHD:  x - at risk\nBHN:  x - at risk' },
  { metric: 'SIMBA Ship Dock Carts/Cages or Container Build\nWeekly Goal: 4', goal: '1 per EOL AM Weekly\n4/Week\nSubmissions must be broken down by shift', example: 'x / 4 - at risk\nFHD: x - at risk\nFHN:  x - at risk\nBHD:  x - at risk\nBHN:  x - at risk' },
  { metric: 'SIMBA Early Symptom Interception', goal: '24 per/Week\nSubmissions must be broken down by shift', example: 'x / 24 - at risk\nFHD: x - at risk\nFHN:  x - at risk\nBHD:  x - at risk\nBHN:  x - at risk' },
  { metric: 'Instacleans\nSubmitted | Graded', goal: '4/Week | 1 per 4 corner shift\n4/4 graded', example: 'FHD: 1 submitted, 1 graded\nFHN: 1 submitted, 1 graded\nBHD: 1 submitted, 0 graded\nBHN: 1 submitted, 0 graded' },
  { metric: 'NSXD Inbound Trailer Audits', goal: 'Ops: 70 weekly (5/shift)', example: 'x / 70' },
  { metric: 'NSXD Outbound Trailer Audits', goal: 'Ops: 140 weekly (10/shift)', example: 'x / 140' },
  { metric: 'Weekly Safety Engagements/NASA', goal: 'Per Specialist: \n4 SE  / 4 NASA', example: 'FHD: 4 SE / 4 NASA\nFHN: 4 SE / 4 NASA\nBHD: 4 SE / 4 NASA\nBHN: 4 SE / 4  NASA' },
  { metric: 'Cast Completion %', goal: '%', example: '73.8%' },
  { metric: 'Conformance %', goal: '%', example: '92.25%' },
  { metric: 'Past Due %', goal: '%', example: '7.75%' },
  { section: 'Wellness' },
  { metric: 'Total Open Cases', goal: '8', example: '8' },
  { metric: 'ICares Due', goal: '4', example: '4' },
  { metric: 'ICare Completion % (WTD)', goal: '1', example: '1' },
  { metric: 'Open Case - Follow-up Completion %', goal: '1', example: '1' },
  { metric: 'Claim Filed - Follow-up Completion %', goal: '1', example: '1' },
  { metric: 'Pending Accommodations', goal: '0 WR pending >1 hour', example: '0 pending' },
  { metric: 'Wellness Weekday Completion', goal: '%', example: '85%' },
  { section: 'PXT' },
  { metric: 'Reminders/Announcements', goal: 'N/A', example: 'Forte - Employee Portion due January 24th, 2027 by 11:59PM Local Time' },
  { subhead: 'PRIOR DAY METRICS' },
  { metric: 'Attendance (by Hours) \nActual vs. Planned', goal: '', example: 'Attendance - XX.XX% on a plan of XX.XX% resulting in a X.XX% variance to plan' },
  { metric: 'VET/VTO/FLEX Acceptance', goal: '', example: 'Example (hrs accepted vs. hrs offered) VET - 650 vs. 718 = 90% \nVTO - 210 vs. 400 = 52%\nFLEX - 1200 vs. 1200 = 100%' },
  { metric: 'Engage Reporting', goal: '>95% - all campaigns', example: 'Example Engage Campaign Total: 100%\nBirthday: 100%\nExperience: 100%\nMilestone: 100%\nRetention: 100%' },
  { subhead: 'CURRENT DAY METRICS' },
  { metric: 'Fun Calendar', goal: 'Fun! :)', example: 'Snack Attack' },
  { metric: 'Attendance (by Headcount) \nOn Prem vs. Planned', goal: '', example: 'Attendance %: 82.67%\nHC On-Prem: 216\nHC Planned: 261\nRisks/Impacts: Operations state no risk to production' },
  { metric: 'ADAPTs', goal: '<7 days to deliver', example: 'ADAPTs On-Prem: 21\nADAPTs Dwelling >7 days: 5  Actions/Ownerships: AM Jeremie Meyer will own delivering dwelling On-Prem ADAPTs' },
  { metric: 'Schedule Risks', goal: '0', example: '>12 Hours: 0\n>60 Hours: 1\n>6 Consecutive: 0 \nActions/Ownerships: TOM team has been notified of TA at risk of going over 60 hours.' },
  { metric: 'Apollo USNS Gemba Audit', goal: '2/day Monday-Friday', example: '2/2 Completed' },
  { metric: 'Thrive.Develop', goal: 'MTD\n1. Thrive\n2. Thrive Completed vs Total Required', example: '1. PA Thrives due this week\n2. 2/30 completed. 6%.' },
  { metric: 'MyVoice - VOA', goal: '0 Pending', example: 'Pending Response: 1\nPending Closure: 2\nDwelling 7 days: 0\nActions (PXT/Sr. Leadership)' },
  { metric: 'DLS Accom Requests', goal: '0', example: 'Number Pending' },
  { section: 'Quatily' },
  { metric: 'IOL > 5 days', goal: '', example: '' },
  { metric: 'GIA DMPO', goal: '', example: '' },
  { metric: 'PID DPMO', goal: '', example: '' },
  { metric: 'UCO DPMO', goal: '', example: '' },
  { metric: 'Missed Scans', goal: '', example: '' },
  { metric: 'Ship Failed Moves', goal: '', example: '' },
  { metric: 'SFM Audit', goal: 'Required for each top offender', example: '' },
  { metric: 'EOL Cage Audits', goal: 'Operations: 2 per quarter (8 per shift)', example: '' },
  { metric: 'Review Atlas for Open Action Items', goal: '', example: '' },
  { metric: 'Coaching Effectiveness 7 Days', goal: '', example: '' },
  { metric: 'PalletHawk Compliance', goal: '', example: '' },
  { metric: 'Piles', goal: '', example: '' },
  { metric: 'PID Missing CSX Prior Day', goal: '', example: '' },
  { metric: 'Review Prior Day Force Bind Pallets', goal: '', example: '' },
  { metric: 'PPA Compliance', goal: '', example: '' },
  { metric: '# Of Load Defects and VRID', goal: '', example: '' },
  { metric: 'RTD Trailers Dwelling >5 Days', goal: '', example: '' },
  { section: 'Learning' },
  { metric: 'Review the Learning Slack Training', goal: 'N/A', example: 'Outbound Cross train Q2\nGTDR Annual Recert Q2\n5 AAs onsite for Wildfire Training' },
  { metric: 'NexGen Training Completion', goal: '1', example: 'PA scheduled for NexGen workshop \n7/3/26' },
  { metric: 'PA/AM OPTT (Learn Courses)', goal: '1', example: 'AM due 7/26' },
  { metric: 'T3+ Training Needs', goal: 'N/A', example: 'PAs due for Jam clear annual recerts' },
  { metric: 'P4L', goal: '1', example: 'AM on track' },
  { metric: 'AMB Hour Violations', goal: '0', example: '0' },
  { metric: 'AMB Needs', goal: 'None', example: 'IB on BHDs' },
  { metric: 'GAP Letters to be Delivered or Feedback to Fill Out', goal: 'None', example: '3 pending letters' },
  { metric: 'Permissions At Risk 0 - 30 Days', goal: 'None', example: '5 total - 3 onsite' },
  { metric: 'Standdowns', goal: 'None', example: 'None' },
  { metric: 'PIT Operators at Risk', goal: 'None', example: 'AA needs to drive forklift by 8/3' },
  { metric: 'Hazmat/COC Onsite vs Completed', goal: 'None', example: '1 onsite Q1 - complete' },
  { section: 'TOM' },
  { metric: 'Yard Capacity', goal: 'N/A', example: '0.9' },
  { metric: 'Operational Hostlers vs. Total Hostlers', goal: 'All Operational', example: '1 Hostler Red Tagged' },
  { metric: 'Drivers Staffed vs. Plan', goal: '', example: '3 Staffed' },
  { metric: 'IB Dwell > 10 days', goal: '', example: '' },
  { metric: 'IB over 72 hours', goal: '', example: '' },
  { metric: 'How many red tagged trailers & ECD to repair?', goal: '', example: '' },
  { metric: 'Current average move time', goal: '', example: '' },
  { metric: 'Empty vs. Full Trailers.', goal: '', example: '' },
  { section: 'Production' },
  { metric: 'Daily Volume Goal and Projected Volume by EOS', goal: '', example: '' },
  { metric: 'Actual HC vs. HC needed to process to BB', goal: '', example: '' },
  { metric: '% to BB Day/Week', goal: '', example: '' },
  { metric: 'Shift Projected Cost', goal: '', example: '' },
  { metric: 'Live Priority Score', goal: '', example: '' },
  { metric: 'Dwelling Trailers >5 days (Count, Actions Needed)', goal: '', example: '' },
  { metric: 'Current BL on site', goal: '', example: '' },
  { metric: 'Trailer Pool', goal: '', example: '' },
].map((row, i) => ({ ...row, k: `r${i}` }));  // stable per-row key for DDD/ALPS storage

// Section colors transcribed from the DDD & ALPS.xlsx fills.
const SECTION_COLORS = {
  WHS: '#00CC66', Wellness: '#00CC66', PXT: '#BF8FD0', Quatily: '#FF69B4',
  Learning: '#00B0F0', TOM: '#F4B084', Production: '#FF6B6B',
};
// Light tint of a section color for the metric rows under it.
const tint = (hex, pct = 0.86) => {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const mix = (c) => Math.round(c + (255 - c) * pct);
  return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
};

export default function DDDAlps({ api, site }) {
  const { user } = useContext(AuthContext) || {};
  const who = (user && user.username) || 'unknown';
  const [date, setDate] = useState('');
  const [shift, setShift] = useState('day');
  const [entries, setEntries] = useState({});   // { "<k>": { ddd, alps, goal } }
  const [showExample, setShowExample] = useState(false);  // Example hidden by default
  const [collapsed, setCollapsed] = useState({}); // { "<section>": true }
  const [customRows, setCustomRows] = useState([]); // persistent user-added rows: [{k, section, metric}]
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const pst = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const h = pst.getHours();
    const d = new Date(pst);
    if (h < 6) d.setDate(d.getDate() - 1);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setShift(h >= 8 && h < 19 ? 'day' : 'night');
    // Default: all sections collapsed.
    const allCol = {};
    TEMPLATE.forEach(r => { if (r.section) allCol[r.section] = true; });
    setCollapsed(allCol);
  }, []);

  // Persistent, global custom rows (added via the per-section "+ line item").
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=DDD_ALPS_CUSTOM&shift_type=data`)
      .then(r => r.json())
      .then(d => setCustomRows(Array.isArray(d?.rows) ? d.rows : []))
      .catch(() => setCustomRows([]));
  }, [api, site]);

  const cacheKey = date ? `DDD_ALPS_${date}` : '';
  const load = useCallback(() => {
    if (!date) return;
    fetch(`${api}/api/fclm/cache?site=${site}&date=DDD_ALPS_${date}&shift_type=${shift}`)
      .then(r => r.json())
      .then(d => { setEntries(d && d.entries ? d.entries : {}); setDirty(false); })
      .catch(() => { setEntries({}); setDirty(false); });
  }, [api, site, date, shift]);
  useEffect(() => { load(); }, [load]);
  // Poll so multiple viewers see saved updates (only when not mid-edit).
  useEffect(() => {
    if (!date) return;
    const t = setInterval(() => { if (!dirty) load(); }, 30000);
    return () => clearInterval(t);
  }, [load, date, dirty]);

  const setCell = (k, field, val) => {
    setEntries(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [field]: val } }));
    setDirty(true);
  };

  // --- Custom rows (persistent, global) ------------------------------------
  const persistCustom = (rows) => {
    setCustomRows(rows);
    fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: 'DDD_ALPS_CUSTOM', shift_type: 'data', data: { rows } }),
    }).catch(() => {});
  };
  const addCustomRow = (section) => {
    const k = `c${Date.now()}`;   // stable unique key for this custom row's DDD/ALPS/goal
    persistCustom([...customRows, { k, section, metric: '' }]);
  };
  const setCustomMetric = (k, metric) => persistCustom(customRows.map(r => r.k === k ? { ...r, metric } : r));
  const removeCustomRow = (k) => persistCustom(customRows.filter(r => r.k !== k));
  // custom rows grouped by section (rendered at the end of each section).
  const customBySection = {};
  customRows.forEach(r => { (customBySection[r.section] = customBySection[r.section] || []).push(r); });

  const save = async () => {
    if (!cacheKey) return;
    setSaving(true);
    try {
      await fetch(`${api}/api/fclm/cache`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site, date: cacheKey, shift_type: shift, data: { entries, date, shift, savedBy: who, savedAt: new Date().toISOString() } }),
      });
      setDirty(false);
      setStatus(`Saved ${date} ${shift} ✓`);
      setTimeout(() => setStatus(''), 2500);
    } catch (e) {
      setStatus('Save failed — try again');
    } finally { setSaving(false); }
  };

  const cell = { border: '1px solid #ccc', padding: '1px 6px', fontSize: '0.72rem', verticalAlign: 'top' };
  const hdr = { ...cell, background: '#232f3e', color: '#fff', fontWeight: 600, textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 };
  const taStyle = { width: '100%', minWidth: 150, minHeight: 26, border: '1px solid #ddd', borderRadius: 2, fontSize: '0.72rem', fontFamily: 'inherit', resize: 'vertical', padding: '1px 4px' };
  const pre = { whiteSpace: 'pre-wrap' };

  return (
    <div>
      <h2 className="section-title">DDD / ALPS — Daily Reporting — {site}</h2>
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <label style={{ fontSize: '0.75rem' }}>Shift:{' '}
          <select value={shift} onChange={e => setShift(e.target.value)} style={{ fontSize: '0.75rem' }}>
            <option value="day">Day</option><option value="night">Night</option>
          </select>
        </label>
        <button onClick={() => {
          const anyOpen = TEMPLATE.some(r => r.section && !collapsed[r.section]);
          const next = {}; TEMPLATE.forEach(r => { if (r.section) next[r.section] = anyOpen; }); // if any open -> collapse all; else expand all
          setCollapsed(next);
        }} className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '4px 10px' }}>
          {TEMPLATE.some(r => r.section && !collapsed[r.section]) ? 'Collapse All' : 'Expand All'}
        </button>
        <button onClick={() => setShowExample(s => !s)} className={`btn ${showExample ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '0.72rem', padding: '4px 10px' }}>
          {showExample ? 'Hide Example' : 'Show Example'}
        </button>
        <button onClick={save} disabled={saving} className="btn btn-primary" style={{ fontSize: '0.72rem', padding: '4px 12px', background: dirty ? '#f39c12' : undefined }}>
          {saving ? 'Saving…' : dirty ? '💾 Save *' : '💾 Save'}
        </button>
        <span style={{ fontSize: '0.7rem', color: '#666' }}>Signed in as <strong>{who}</strong>{status ? ` · ${status}` : ''}{dirty && !status ? ' · unsaved changes' : ''}</span>
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...hdr, minWidth: 220 }}>Metric</th>
              <th style={{ ...hdr, minWidth: 110 }}>Goal</th>
              {showExample && <th style={{ ...hdr, minWidth: 180 }}>Example</th>}
              <th style={{ ...hdr, minWidth: 170 }}>DDD</th>
              <th style={{ ...hdr, minWidth: 170 }}>ALPS</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const span = showExample ? 5 : 4;
              let curSection = null;        // section the current row belongs to
              const out = [];
              // Emit a section's custom rows + its "+ Add line item" button
              // (only when the section is expanded).
              const flushSection = (sec) => {
                if (!sec || collapsed[sec]) return;
                const col = SECTION_COLORS[sec] || '#ccc';
                (customBySection[sec] || []).forEach(cr => {
                  const e = entries[cr.k] || {};
                  const goalVal = e.goal !== undefined ? e.goal : '';
                  out.push(
                    <tr key={cr.k} style={{ background: tint(col, 0.9) }}>
                      <td style={{ ...cell, borderLeft: `4px solid ${col}`, maxWidth: 260 }}>
                        <input value={cr.metric} placeholder="New metric…" onChange={ev => setCustomMetric(cr.k, ev.target.value)} style={{ width: '100%', border: '1px solid #ddd', borderRadius: 2, fontSize: '0.72rem', fontWeight: 600, padding: '1px 4px' }} />
                      </td>
                      <td style={cell}><textarea value={goalVal} onChange={ev => setCell(cr.k, 'goal', ev.target.value)} style={{ ...taStyle, minWidth: 100, color: '#555' }} /></td>
                      {showExample && <td style={cell}></td>}
                      <td style={cell}><textarea value={e.ddd || ''} onChange={ev => setCell(cr.k, 'ddd', ev.target.value)} style={taStyle} /></td>
                      <td style={{ ...cell, position: 'relative' }}>
                        <textarea value={e.alps || ''} onChange={ev => setCell(cr.k, 'alps', ev.target.value)} style={taStyle} />
                        <button onClick={() => removeCustomRow(cr.k)} title="Remove this line" style={{ position: 'absolute', top: 1, right: 1, border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: '0.7rem' }}>✕</button>
                      </td>
                    </tr>
                  );
                });
                out.push(
                  <tr key={`add-${sec}`}>
                    <td colSpan={span} style={{ ...cell, textAlign: 'left', background: tint(col, 0.95) }}>
                      <button onClick={() => addCustomRow(sec)} style={{ fontSize: '0.68rem', border: '1px dashed #888', background: '#fff', borderRadius: 3, padding: '1px 8px', cursor: 'pointer', color: '#333' }}>+ Add line item to {sec}</button>
                    </td>
                  </tr>
                );
              };
              TEMPLATE.forEach((row) => {
                if (row.section) {
                  flushSection(curSection);   // close out the previous section first
                  curSection = row.section;
                  const col = SECTION_COLORS[row.section] || '#1b5e20';
                  const isCol = !!collapsed[row.section];
                  out.push(
                    <tr key={row.k}>
                      <td colSpan={span} onClick={() => setCollapsed(p => ({ ...p, [row.section]: !p[row.section] }))}
                        style={{ ...cell, background: col, color: '#111', fontWeight: 800, fontSize: '0.85rem', textAlign: 'left', cursor: 'pointer', userSelect: 'none', position: 'sticky', left: 0 }}>
                        {isCol ? '▸' : '▾'} {row.section}
                      </td>
                    </tr>
                  );
                  return;
                }
                if (collapsed[curSection]) return;   // hide rows under a collapsed section
                if (row.subhead) {
                  const col = SECTION_COLORS[curSection] || '#c8e6c9';
                  out.push(
                    <tr key={row.k}><td colSpan={span} style={{ ...cell, background: tint(col, 0.6), fontWeight: 700, textAlign: 'left' }}>{row.subhead}</td></tr>
                  );
                  return;
                }
                const e = entries[row.k] || {};
                const rowBg = curSection ? tint(SECTION_COLORS[curSection] || '#ffffff', 0.9) : '#fff';
                const goalVal = e.goal !== undefined ? e.goal : (row.goal || '');
                out.push(
                  <tr key={row.k} style={{ background: rowBg }}>
                    <td style={{ ...cell, ...pre, fontWeight: 600, maxWidth: 260, borderLeft: `4px solid ${SECTION_COLORS[curSection] || '#ccc'}` }}>{row.metric}</td>
                    <td style={cell}><textarea value={goalVal} onChange={ev => setCell(row.k, 'goal', ev.target.value)} style={{ ...taStyle, minWidth: 100, color: '#555' }} /></td>
                    {showExample && <td style={{ ...cell, ...pre, color: '#888', fontStyle: 'italic', maxWidth: 220 }}>{row.example}</td>}
                    <td style={cell}><textarea value={e.ddd || ''} onChange={ev => setCell(row.k, 'ddd', ev.target.value)} style={taStyle} /></td>
                    <td style={cell}><textarea value={e.alps || ''} onChange={ev => setCell(row.k, 'alps', ev.target.value)} style={taStyle} /></td>
                  </tr>
                );
              });
              flushSection(curSection);   // last section's customs + add button
              return out;
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
